/**
 * Direct deep-link screen for SP Loan Value lookup.
 */
import { formatInteger } from "./format.js";
import { fetchJson } from "./dashboard-lead-factory-fetch.js";
import { toolbenchRunsUrl } from "./dashboard-urls.js";
import { bindDirectSpLookup, spLookupHistoryHtml } from "./dashboard-sp-lookup.js";
import type { ToolbenchRunsResponse } from "./dashboard-lead-factory-types.js";

export function directSpLookupRequested(): boolean {
  const value = new URLSearchParams(window.location.search).get("sp_lookup")?.trim().toLowerCase() ?? "";
  return value === "1" || value === "true";
}

export async function renderDirectSpLookup(mount: HTMLElement): Promise<void> {
  const runsResponse = await fetchJson<ToolbenchRunsResponse>(`${toolbenchRunsUrl.split("?")[0]}?tool_id=sp_code_lookup&limit=10`).catch(
    () => ({ generatedAt: new Date().toISOString(), runs: [] })
  );
  mount.innerHTML = `
    <main class="dashboard-shell sp-lookup-direct" data-sp-lookup-direct>
      <section class="sp-lookup-hero">
        <div>
          <p class="eyebrow">Lead Factory Research</p>
          <h1>SP Loan Value</h1>
          <p>Enter a North Carolina SP number to collect the available case and Notice evidence and estimate the remaining principal as an expected opening-bid proxy.</p>
        </div>
        <div class="sp-lookup-hero-actions">
          <a class="button secondary" href="/">Dashboard</a>
          <a class="button secondary" href="/#research-tools">Toolbench</a>
        </div>
      </section>
      <section class="sp-lookup-grid">
        <form class="panel sp-lookup-form" data-sp-lookup-form>
          <label for="sp-lookup-number">SP number</label>
          <div class="sp-lookup-entry">
            <input id="sp-lookup-number" name="spNumber" type="text" inputmode="text" autocomplete="off" placeholder="25SP001417-170" maxlength="80" required data-sp-lookup-number>
            <button class="button primary" type="submit">Look Up Case</button>
          </div>
          <p class="subtle">Uses NC eCourts Smart Search. Results preserve every available source field and document reference.</p>
          <p class="action-status" role="status" aria-live="polite" data-sp-lookup-status></p>
        </form>
        <aside class="panel sp-assumption-card" aria-label="fixed loan assumptions">
          <h2>Fixed calculation assumptions</h2>
          <dl>
            <div><dt>Loan</dt><dd>30-year fixed</dd></div>
            <div><dt>Rate</dt><dd>Freddie Mac PMMS annual average for the origination year</dd></div>
            <div><dt>Payments</dt><dd>Made normally through today minus twelve months</dd></div>
            <div><dt>Output</dt><dd>Remaining principal used as an expected opening-bid proxy</dd></div>
          </dl>
        </aside>
      </section>
      <section class="sp-lookup-result" data-sp-lookup-result aria-live="polite">
        <div class="panel sp-empty-result">
          <h2>Lookup result</h2>
          <p class="subtle">Submit an SP number to begin. The page will show partial evidence and typed review states when a complete estimate is unavailable.</p>
        </div>
      </section>
      <section class="panel sp-lookup-history">
        <div class="panel-heading"><h2>Recent SP lookups</h2><span class="subtle" data-sp-lookup-history-count>${formatInteger(runsResponse.runs.length)} loaded</span></div>
        <div class="sp-history-list" data-sp-lookup-history>
          ${runsResponse.runs.map(spLookupHistoryHtml).join("") || `<p class="empty">No SP lookup has been submitted yet.</p>`}
        </div>
      </section>
    </main>
  `;
  bindDirectSpLookup();
}
